import React, { useEffect, useState } from "react";
import { Card, CardBody, Col, Container, Row, Input, Label, Button } from "reactstrap";
import Select from "react-select";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { getMovingHistories } from "../../../helpers/urlConfig/apiFunctions";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { documentTitle } from "../../../helpers/documentTitle";
import { dateChangerFunc, dateParser } from "../../../helpers/dateParser";
import errorResponseHandler from "../../../helpers/errorResponseHandler";
import { History } from "swiper";

const StocksMovingHistoryList = () => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [customerNameOptions, setCustomerNameOptions] = useState([]);
  const [placeOptions, setPlaceOptions] = useState([]);
  const [mobileNumberOptions, setMobileNumberOptions] = useState([]);

  const [selectedCustomerName, setSelectedCustomerName] = useState(null);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [selectedMobileNumber, setSelectedMobileNumber] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  documentTitle("Customers");

  const fetchData = async () => {
    try {
      const response = await getMovingHistories();

      if (response.statusCode === 200) {
        setCustomers(response.data);
        setFilteredCustomers(response.data);

        setCustomerNameOptions(
          response.data.map((customer) => ({
            value: customer.customer_name,
            label: customer.customer_name,
          }))
        );

        setPlaceOptions(
          response.data
            .map((customer) => ({
              value: customer.place,
              label: customer.place,
            }))
            .filter((place, index, self) => {
              return index === self.findIndex((p) => p.value === place.value);
            })
        );

        setMobileNumberOptions(
          response.data
            .map((customer) => ({
              value: customer.phone_number,
              label: customer.phone_number,
            }))
            .filter(
              (phone, index, self) =>
                index === self.findIndex((p) => p.value === phone.value)
            )
        );
      } else {
        errorResponseHandler(response);
      }
    } catch (error) {
      console.error(error.message);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredCustomers);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Customers");
    XLSX.writeFile(workbook, "Customers.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text("Customer List", 14, 10);
    doc.autoTable({
      head: [["SL.No", "Customer", "Phone", "Place", "Address", "GST Details"]],
      body: filteredCustomers.map((customer, index) => [
        index + 1,
        customer.customer_name,
        customer.phone_number,
        customer.place,
        customer.address,
        customer.gst_details,
      ]),
    });
    doc.save("Customers.pdf");
  };

  const handleFilter = () => {
    let filtered = customers;

    if (selectedCustomerName) {
      filtered = filtered.filter(
        (customer) => customer.customer_name === selectedCustomerName.value
      );
    }

    if (selectedPlace) {
      filtered = filtered.filter(
        (customer) => customer.place === selectedPlace.value
      );
    }

    if (selectedMobileNumber) {
      filtered = filtered.filter(
        (customer) => customer.phone_number === selectedMobileNumber.value
      );
    }

    if (fromDate) {
      filtered = filtered.filter(
        (customer) => dateParser(customer.created_at) >= fromDate
      );
    }

    if (toDate) {
      filtered = filtered.filter(
        (customer) => dateParser(customer.created_at) <= toDate
      );
    }

    setFilteredCustomers(filtered);
  };

  useEffect(() => {
    handleFilter();
  }, [
    selectedCustomerName,
    selectedPlace,
    selectedMobileNumber,
    fromDate,
    toDate,
  ]);

  let pairCount = 0;

  const getHUIDs = (productTypes) => {
    let pairedHUIDs = [];
    if (productTypes.huid == null || productTypes.huid == " ") {
      const parsedHUIDs = JSON.parse(productTypes.pairs);
      pairCount = parsedHUIDs.length;
      parsedHUIDs.map((item) => {
        pairedHUIDs.push(item.huid);
      });

      return pairedHUIDs.map((huid, index) => (
        <span
          key={index}
          style={{ fontSize: "10px" }}
          className="badge bg-danger me-1"
        >
          {huid}
        </span>
      ));
    } else {
      return <span className="badge bg-success">{productTypes.huid}</span>;
    }
  };

  const clearDateFilters = () => {
    setFromDate("");
    setToDate("");
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Stock Moving History"
          pageTitle="Customer"
          exportToExcel={exportToExcel}
          exportToPDF={exportToPDF}
        />
        <Row className="mb-3">
          <Col md={3}>
            <Label>From Date:</Label>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </Col>
          <Col md={3}>
            <Label>To Date:</Label>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </Col>
          <Col md={1} className="pt-4">
            <Button
              color="secondary"
              className="btn-sm w-100"
              onClick={clearDateFilters}
              disabled={!fromDate && !toDate} // Disable if no dates are selected
            >
              Clear
            </Button>
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <div id="customerList">
                  <div className="table-responsive table-card mt-3 mb-1">
                    <table className="table align-middle table-nowrap">
                      <thead className="table-light">
                        <tr>
                          <th>Tag ID</th>
                          <th>Product</th>
                          <th>Type</th>
                          <th>Model Name</th>
                          <th>HUID</th>
                          {/* <th>Sets</th> */}
                          <th>Gram</th>
                          <th>Description</th>
                          <th>Created At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCustomers.map((customer, index) => (
                          <tr key={index}>
                            <td>{customer.unique_stock_id}</td>
                            <td>{customer.product_name || "-"}</td>
                            <td>{customer.product_type_name || "-"}</td>
                            <td>{customer.model_name || "-"}</td>
                            <td>{getHUIDs(customer)}</td>
                            {/* <td>{customer.pairs}</td> */}
                            <td>{customer.gram || "-"}</td>
                            <td>{customer.description || "-"}</td>
                            <td>
                              {dateChangerFunc(
                                dateParser(customer.created_at)
                              ) || "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {filteredCustomers.length === 0 && (
                      <div className="text-center mt-4">
                        <p>No moving history found.</p>
                      </div>
                    )}
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default StocksMovingHistoryList;
